import React, { useState } from "react";
import { Box, Typography } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import FaxViewerPopup from "./FaxViewerPopup";

/**
 * CallDetailsHeader - Header component with patient info and tab navigation
 */
const CallDetailsHeader = ({
  memberName,
  memberId,
  onBack,
  selectedTab,
  onTabChange,
  currentTheme,
  themeMode,
  // Recording navigation props
  totalAttempts = 1,
  currentAttemptIndex = 0,
  onPreviousRecording,
  onNextRecording,
  // Patient navigation props
  onNextPatient,
  onPreviousPatient,
  isFirstPatient,
  isLastPatient,
  // Fax props
  faxSignedUrl,
}) => {
  const [showFaxPopup, setShowFaxPopup] = useState(false);
  const tabStyle = (isActive) => ({
    px: 3,
    py: 1.5,
    backgroundColor: isActive ? "#e63e2b" : themeMode === "dark" ? "#3e3c57" : "#e3e2e5",
    color: isActive ? "#ffffff" : currentTheme.text.primary,
    borderRadius: "12px",
    cursor: "pointer",
    fontWeight: 500,
    fontSize: "0.95rem",
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: isActive ? "#d32f2f" : themeMode === "dark" ? "#4e4c67" : "#d3d2d5",
    },
  });

  return (
    <Box>
      {/* Back button and patient info */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 3,
        }}
      >
        {/* Left side: Back button + Patient info */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box
            onClick={onBack}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 40,
              height: 40,
              borderRadius: "8px",
              backgroundColor: themeMode === "dark" ? "#3e3c57" : "#e3e2e5",
              cursor: "pointer",
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: themeMode === "dark" ? "#4e4c67" : "#d3d2d5",
              },
            }}
          >
            <ArrowBackIcon sx={{ color: currentTheme.text.primary }} />
          </Box>

          <Box>
            <Typography
              variant="h5"
              sx={{
                color: currentTheme.text.primary,
                fontWeight: 600,
                mb: 0.5,
              }}
            >
              {memberName || "Unknown Member"}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: currentTheme.text.secondary,
              }}
            >
              Patient ID: {memberId || "N/A"}
            </Typography>
          </Box>
        </Box>

        {/* Right side: Patient navigation */}
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Box
            onClick={onPreviousPatient}
            sx={{
              px: 3,
              py: 1.5,
              backgroundColor: isFirstPatient ? (themeMode === "dark" ? "#3e3c57" : "#ccc") : "#e63e2b",
              color: "#ffffff",
              borderRadius: "12px",
              cursor: isFirstPatient ? "not-allowed" : "pointer",
              fontWeight: 500,
              fontSize: "0.95rem",
              opacity: isFirstPatient ? 0.5 : 1,
              transition: "all 0.2s ease",
              "&:hover": !isFirstPatient ? { backgroundColor: "#d32f2f" } : {},
            }}
          >
            ‹ Previous Patient
          </Box>
          <Box
            onClick={onNextPatient}
            sx={{
              px: 3,
              py: 1.5,
              backgroundColor: isLastPatient ? (themeMode === "dark" ? "#3e3c57" : "#ccc") : "#e63e2b",
              color: "#ffffff",
              borderRadius: "12px",
              cursor: isLastPatient ? "not-allowed" : "pointer",
              fontWeight: 500,
              fontSize: "0.95rem",
              opacity: isLastPatient ? 0.5 : 1,
              transition: "all 0.2s ease",
              "&:hover": !isLastPatient ? { backgroundColor: "#d32f2f" } : {},
            }}
          >
            Next Patient ›
          </Box>
        </Box>
      </Box>

      {/* Tab navigation with recording navigation */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        {/* Left side: Tabs */}
        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
          <Box
            onClick={() => onTabChange("summary")}
            sx={tabStyle(selectedTab === "summary")}
          >
            Call Summary
          </Box>
          
          {/* Vertical Divider/Pipe - Always visible */}
          <Box
            sx={{
              width: "0.125rem",
              height: "2rem",
              backgroundColor: themeMode === "dark" ? "rgba(255, 255, 255, 0.2)" : "rgba(0, 0, 0, 0.15)",
              mx: 0.5,
            }}
          />
          
          <Box
            onClick={() => onTabChange("transcript")}
            sx={tabStyle(selectedTab === "transcript")}
          >
            Call Details with Transcript
          </Box>
          
          {/* Fax Details Button - Only show when faxSignedUrl is available */}
          {faxSignedUrl && (
            <Box
              onClick={() => setShowFaxPopup(true)}
              sx={{
                px: 3,
                py: 1.5,
                backgroundColor: themeMode === "dark" ? "#3e3c57" : "#e3e2e5",
                color: currentTheme.text.primary,
                borderRadius: "12px",
                cursor: "pointer",
                fontWeight: 500,
                fontSize: "0.95rem",
                transition: "all 0.2s ease",
                display: "flex",
                alignItems: "center",
                gap: 1,
                "&:hover": {
                  backgroundColor: themeMode === "dark" ? "#4e4c67" : "#d3d2d5",
                },
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M19 9h-1V4H8v5H5c-1.66 0-3 1.34-3 3v7c0 1.1.9 2 2 2h2v-3h12v3h2c1.1 0 2-.9 2-2v-7c0-1.66-1.34-3-3-3zM10 6h6v3h-6V6zm8 12H8v-4h10v4zm2-5c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1z" fill="currentColor"/>
              </svg>
              Fax Details
            </Box>
          )}
        </Box>

        {/* Right side: Recording navigation & Reschedule */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          {/* Recording navigation (only show on transcript tab with multiple attempts) */}
          {totalAttempts > 1 && selectedTab === "transcript" && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Box
                onClick={onPreviousRecording}
                sx={{
                  px: 2,
                  py: 1,
                  backgroundColor: currentAttemptIndex === 0 ? (themeMode === "dark" ? "#3e3c57" : "#ccc") : "#e63e2b",
                  color: "#fff",
                  borderRadius: "8px",
                  cursor: currentAttemptIndex === 0 ? "not-allowed" : "pointer",
                  opacity: currentAttemptIndex === 0 ? 0.5 : 1,
                  fontSize: "0.9rem",
                  fontWeight: 500,
                  transition: "all 0.2s ease",
                  "&:hover": currentAttemptIndex > 0 ? { backgroundColor: "#d32f2f" } : {},
                }}
              >
                ‹ Previous
              </Box>

              <Typography
                sx={{
                  color: currentTheme.text.primary,
                  fontSize: "0.9rem",
                  fontWeight: 500,
                }}
              >
                {currentAttemptIndex + 1}/{totalAttempts} Recordings
              </Typography>

              <Box
                onClick={onNextRecording}
                sx={{
                  px: 2,
                  py: 1,
                  backgroundColor: currentAttemptIndex === totalAttempts - 1 ? (themeMode === "dark" ? "#3e3c57" : "#ccc") : "#e63e2b",
                  color: "#fff",
                  borderRadius: "8px",
                  cursor: currentAttemptIndex === totalAttempts - 1 ? "not-allowed" : "pointer",
                  opacity: currentAttemptIndex === totalAttempts - 1 ? 0.5 : 1,
                  fontSize: "0.9rem",
                  fontWeight: 500,
                  transition: "all 0.2s ease",
                  "&:hover": currentAttemptIndex < totalAttempts - 1 ? { backgroundColor: "#d32f2f" } : {},
                }}
              >
                Next ›
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      {/* Fax Viewer Popup */}
      <FaxViewerPopup
        visible={showFaxPopup}
        faxUrl={faxSignedUrl}
        onClose={() => setShowFaxPopup(false)}
      />
    </Box>
  );
};

export default CallDetailsHeader;

